import * as THREE from 'three';
import {
  FaceLandmarker,
  FilesetResolver,
} from 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0/+esm';
import EventDispatcher from './common/EventDispatcher';
import AssetLoader from './components/AssetLoader';
import { Global } from './common/global';
import RAPIER from '@dimforge/rapier3d-compat';
import './styles/main.scss';
import './styles/Home.scss';
import './styles/Instruction.scss';
import Ball from './components/Ball';
import { isMobile } from 'mobile-device-detect';
import Stats from 'https://cdnjs.cloudflare.com/ajax/libs/stats.js/17/Stats.js';
import UI from './components/UI/UI';
import Sounds from './components/Sounds';

// --- OBJECT POOLING (Prevents Garbage Collection Stutter) ---
const _m = new THREE.Matrix4();
const _mirror = new THREE.Matrix4().makeScale(-1, 1, 1);
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();

let lastTime = 0;
const physicsStep = 1 / 120;
let accumulator = 0;
let printed = false;
export default class Game extends THREE.Scene {
  sizes = { width: window.innerWidth, height: window.innerHeight };

  constructor() {
    super();
    window.addEventListener('resize', this.onResize.bind(this));
    this.lastVideoTime = -1;
    this.faceModel = null;
    this.faceLandmarker = null;
    this.video = null;
    this.smoothedZ = 0;
    this.isFirefox = false;
    this.canRender = false;
    this.world = null;
    this.faceBody = null;
    this.doDetect = true;
    this.detectInProgress = false;
    // Performance Throttling
    this.detectionInterval = 2; // Run MediaPipe every 2nd frame
    this.frameCount = 0;
  }

  async init() {
    Global.scoreTotal = 0;
    document.querySelector('#score_sec .label').innerHTML =
      `${Global.scoreTotal < 10 ? '0' : ''}${Global.scoreTotal}`;
    // this.stats = new Stats();
    // this.stats.showPanel(0);
    // document.body.appendChild(this.stats.dom);

    this.clock = new THREE.Clock();
    this.addEventListeners();
    this.physicsTimeScale = isMobile ? 1 : 1;

    this.camera = new THREE.PerspectiveCamera(
      60,
      this.sizes.width / this.sizes.height,
      0.1,
      1000
    );
    this.camera.position.set(0, 0, 0);
    this.add(this.camera);

    this.canvas = document.querySelector('canvas.webgl');
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    this.setupRenderer();

    this.add(new THREE.AmbientLight(0xffffff, 1.0));
    const sun = new THREE.DirectionalLight(0xffffff, 1.5);
    sun.position.set(0, 5, 5);
    this.add(sun);

    await RAPIER.init();
    // SETTING IDEAL GRAVITY FOR SCALE 10
    this.world = new RAPIER.World({ x: 0, y: -9.81 * 1.2, z: 0 });
    Global.physicsWorld = this.world;
    this.eventQueue = new RAPIER.EventQueue(true);
    this.debugGeometry = new THREE.BufferGeometry();
    this.debugMesh = new THREE.LineSegments(
      this.debugGeometry,
      new THREE.LineBasicMaterial({ vertexColors: true, depthTest: false })
    );
    this.debugMesh.frustumCulled = false;
    this.debugMesh.visible = false;
    this.add(this.debugMesh);

    await this.initMediaPipe();

    this.assetLoader = new AssetLoader(this);
    this.assetLoader.init();
    this.assetLoader.prepare();

    this.gameSounds = new Sounds(this, this.camera);
    this.gameSounds.init();
    this.ball = new Ball(this);
    this.ball.init();

    this.ui = new UI();
    this.ui.init();

    this.onResize();
    this.tick(0);
  }

  async initMediaPipe() {
    this.isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    const modelPath =
      'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    this.faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: modelPath,
        delegate: this.isFirefox ? 'CPU' : 'GPU',
      },
      outputFacialTransformationMatrixes: true,
      runningMode: this.isFirefox ? 'IMAGE' : 'VIDEO',
      outputFaceBlendshapes: false,

      minFaceDetectionConfidence: 0.5,
      numFaces: 1,
    });

    this.video = document.createElement('video');
    this.video.id = 'video_stream';
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: isMobile ? 640 : 1280,
        height: isMobile ? 480 : 720,
      }, // Lowering camera res helps performance significantly
    });

    this.video.srcObject = stream;
    this.video.setAttribute('playsinline', '');
    await this.video.play();

    Object.assign(this.video.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      objectFit: 'cover',
      zIndex: '-1',

      transform: 'scaleX(-1)',
    });
    document.body.appendChild(this.video);
  }

  tick(now) {
    if (!this.canRender || !Global.isGameActive) {
      window.requestAnimationFrame(this.tick.bind(this));
      return;
    }
    this.stats && this.stats.begin();

    const delta = (now - lastTime) / 1000;
    lastTime = now;

    // --- MEDIAPIPE TRACKING (THROTTLED) ---
    this.frameCount++;
    // console.log(Math.floor(this.frameCount % this.detectionInterval));
    // this.doDetect = !this.doDetect;
    if (this.frameCount % (this.isFirefox ? 3 : 2) == 0) {
      this.updateFaceTracking(now);
    }
    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'update',
        data: {
          pos: this.faceModel.position,
          quot: this.faceModel.quaternion,
        },
      },
    });
    // --- FIXED TIMESTEP PHYSICS ---
    if (this.world) {
      accumulator += Math.min(delta, 0.05); // Cap delta to avoid "spiral of death"
      while (accumulator >= physicsStep) {
        this.world.step();

        this.world.step(this.eventQueue);

        this.eventQueue.drainCollisionEvents((handle1, handle2, started) => {
          this.processCollision(handle1, handle2);
        });
        accumulator -= physicsStep;
      }
    }

    // --- SYNC PHYSICS TO VISUALS ---
    // if (this.faceBody && this.faceModel) {
    //   const t = this.faceBody.translation();
    //   const r = this.faceBody.rotation();
    //   this.faceModel.position.set(t.x, t.y, t.z);
    //   this.faceModel.quaternion.set(r.x, r.y, r.z, r.w);
    // }
    if (this.faceBody && this.faceModel) {
      // 1. Get raw physics data
      const t = this.faceBody.translation();
      const r = this.faceBody.rotation();

      // 2. Define smoothing factor (0.05 is very smooth, 0.2 is snappier)
      const zLerpFactor = 0.1;

      // 3. Apply Lerp: current = current + (target - current) * factor
      this.smoothedZ += (t.z - this.smoothedZ) * zLerpFactor;

      // 4. Update Visuals
      // X and Y stay raw for instant responsiveness, Z is smoothed
      this.faceModel.position.set(t.x, t.y, this.smoothedZ);

      // Update rotation (Consider using slerp here if rotation is also jumpy)
      this.faceModel.quaternion.set(r.x, r.y, r.z, r.w);
    }

    this.renderer.render(this, this.camera);

    this.stats && this.stats.end();
    window.requestAnimationFrame(this.tick.bind(this));
  }
  processCollision(handle1, handle2) {
    this.ballLaunched = false;
    const body1 = Global.physicsWorld.getRigidBody(handle1);
    const body2 = Global.physicsWorld.getRigidBody(handle2);

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'play_sound',
        data: {
          sndKey: 'head',
        },
      },
    });

    Global.scoreTotal++;
    document.querySelector('#score_sec .label').innerHTML =
      `${Global.scoreTotal < 10 ? '0' : ''}${Global.scoreTotal}`;
    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'apply_ball_velocity',
        data: {
          shake_fact: 0.2,
          shake_time: 0.2,
        },
      },
    });
    return;
    if (body1.userData.name.includes('bar_segment')) {
      const _score = this.scoreData[body1.userData.name].score;
      const _color = this.scoreData[body1.userData.name].color;
      Global.scoreTotal += this.scoreData[body1.userData.name].score;
      gsap.to(`#_${_score}_${_color}`, {
        scale: 1, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.out', // Keeps the speed constant
        delay: 0,
      });
      gsap.to(`#_${_score}_${_color}`, {
        scale: 0, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.in', // Keeps the speed constant
        delay: 1,
      });
      this.events.dispatchEvent({
        type: 'CAMERA_EVENTS',
        message: {
          event_type: 'shake_camera',
          data: {
            shake_fact: 0.2,
            shake_time: 0.2,
          },
        },
      });
      document.querySelector('.score_sec .score').innerHTML =
        `SCORE ${Global.scoreTotal}`;
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'barHit' } },
      });
      gsap.delayedCall(0.1, () => {
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'cheer' } },
        });
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'score' } },
        });
      });
    } else {
      gsap.to(`#miss_icon`, {
        scale: 1, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.out', // Keeps the speed constant
        delay: 0,
      });
      gsap.to(`#miss_icon`, {
        scale: 0, // Rotates to 360 degrees
        duration: 0.4,
        ease: 'back.in', // Keeps the speed constant
        delay: 1,
      });
      if (body1.userData.name.includes('net')) {
        this.events.dispatchEvent({
          type: 'SOUND_EVENTS',
          message: { event_type: 'play_sound', data: { sndKey: 'net' } },
        });
      }
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: { event_type: 'play_sound', data: { sndKey: 'miss' } },
      });
    }
    return;
    // Check if either body is our ball
    const ball =
      body1 === this.ball.ballBody
        ? body1
        : body2 === this.ball.ballBody
          ? body2
          : null;
    const other = ball === body1 ? body2 : body1;

    if (ball && other) {
      // This is where you call your specific handler!
      this.ball.handleCollision(other);
    }
  }
  updateFaceTracking(now) {
    // if (this.detectInProgress) return;
    // if (this.video && this.video.currentTime == this.lastVideoTime) {
    //   return;
    // }
    this.detectInProgress = true;
    if (this.video?.readyState >= 2 && this.faceBody) {
      try {
        let results = null;
        if (this.isFirefox) {
          results = this.faceLandmarker.detect(this.video);
        } else {
          results = this.faceLandmarker.detectForVideo(this.video, now);
        }

        // return;
        if (
          results.facialTransformationMatrixes &&
          results.facialTransformationMatrixes.length > 0
        ) {
          _m.fromArray(results.facialTransformationMatrixes[0].data);
          _m.premultiply(_mirror);
          _m.decompose(_pos, _quat, _scale);

          // Update Kinematic Body
          this.faceBody.setNextKinematicTranslation(_pos);
          this.faceBody.setNextKinematicRotation(_quat);
        }
      } catch (error) {
        this.detectInProgress = false;
        // console.error('Face tracking failed:', error);
        // Game continues, just skips this frame
      }
    }
  }

  setupRenderer() {
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
  }

  addEventListeners() {
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('LOADER_EVENTS', (data) => {
      if (data['message']['event_type'] === 'load_complete') {
        this.canRender = true;
        document.querySelector('#splash_scr').classList.remove('active');

        this.modelWrapper = new THREE.Group();
        Global.modelWrapper = this.modelWrapper;
        this.faceModel = Global.assets['glbs']['head']['asset'].scene;
        this.faceModel.scale.set(10, 10, 10);
        this.modelWrapper.add(this.faceModel);
        this.add(this.modelWrapper);
        this.faceModel = this.modelWrapper;
        this.modelWrapper.visible = false;

        const bodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased();
        this.faceBody = this.world.createRigidBody(bodyDesc);

        const collider = RAPIER.ColliderDesc.ball(8.0)
          .setTranslation(0, 6, 0)
          .setRestitution(0.5); // Lowered for better control

        this.world.createCollider(collider, this.faceBody);
        this.setUpGame();
      }
    });
    this.events.addEventListener('GAME_EVENTS', (data) => {
      if (data['message']['event_type'] === 'game_over') {
        Global.isGameActive = false;
        this.events.dispatchEvent({
          type: 'GAMESCENE_EVENTS',
          message: {
            event_type: 'show_screen',
            data: {
              screenKey: 'Score',
            },
          },
        });
        document.querySelector('#video_stream').classList.remove('active');
      }
    });
    this.events.addEventListener('GAMESCENE_EVENTS', (data) => {
      if (data['message']['event_type'] === 'prepare_to_start') {
        Global.scoreTotal = 0;
        document.querySelector('#score_sec .label').innerHTML =
          `${Global.scoreTotal < 10 ? '0' : ''}${Global.scoreTotal}`;
        Global.isGameActive = true;
        document.querySelector('#video_stream').classList.add('active');
      }
      if (data['message']['event_type'] === 'replay_game') {
        Global.gameTry++;
        Global.isGameActive = true;
        this.events.dispatchEvent({
          type: 'GAMESCENE_EVENTS',
          message: {
            event_type: 'show_screen',
            data: {
              screenKey: 'Instruction',
            },
          },
        });
        this.events.dispatchEvent({
          type: 'GAMESCENE_EVENTS',
          message: {
            event_type: 'prepare_to_start',
            data: {},
          },
        });
      }
    });
  }

  setUpGame() {
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Home',
        },
      },
    });
    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'show_screen',
    //     data: {
    //       screenKey: 'Instruction',
    //     },
    //   },
    // });
    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'prepare_to_start',
    //     data: {},
    //   },
    // });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: { event_type: 'create_scene', data: {} },
    });
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'init_sounds',
        data: {},
      },
    });
  }

  onResize() {
    this.sizes.width = window.innerWidth;
    this.sizes.height = window.innerHeight;
    this.camera.aspect = this.sizes.width / this.sizes.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.sizes.width, this.sizes.height);
  }
}

window.onload = () => {
  if (!isMobile) {
    document.querySelector('#homeScr').classList.add('desktop');
    document.querySelector('#game_view').classList.add('desktop');
    document.querySelector('#scoreScr').classList.add('desktop');
    document.querySelector('#instrScr').classList.add('desktop');
  } else {
    document.querySelector('#homeScr .bg2').src = `./ui/BG02.png`;

    document.querySelector('#scoreScr .bg2').src = `./ui/BG02.png`;
  }
  new Game().init();
};
